import { useSearchParams } from 'react-router'
import { ReactComponent as Logo } from '../../assets/logo.svg'
import { env } from '../../config/env'
import Button from '../../ui/Button'
import './Auth.css'
import { useEffect, useState } from 'react'
import api from '../../api'
import { AuthCheckResponse, AuthMethod } from '../../types'
import FormWrapper from '../../ui/form/FormWrapper'
import TextInput from '../../ui/form/TextInput'
import { Alert } from '../../ui'
import { useTranslation } from 'react-i18next'

interface LoginParams {
    email: string
    password?: string
}

type LoginStep = 'methods' | 'basic' | 'email-input' | 'email-options' | 'email-sent'

export default function Login() {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const [methods, setMethods] = useState<AuthMethod[]>()
    const [step, setStep] = useState<LoginStep>('methods')
    const [email, setEmail] = useState<string>('')
    const [ssoInfo, setSsoInfo] = useState<AuthCheckResponse>()
    const [message, setMessage] = useState<string>()
    const [error] = useState<string>(searchParams.get('error') ?? '')

    const redirect = searchParams.get('r') ?? '/'

    const handleRedirect = (driver: string, email?: string) => {
        window.location.href = `${env.api.baseURL}/auth/login/${driver}?r=${redirect}&email=${email}`
    }

    const handleMethod = (method: AuthMethod) => {
        if (method.driver === 'basic') {
            setStep('basic')
        } else if (method.driver === 'email') {
            setStep('email-input')
        } else {
            handleRedirect(method.driver)
        }
    }

    const handleBasicLogin = async ({ email, password }: LoginParams) => {
        await api.auth.basicAuth(email, password!, redirect)
    }

    const handleEmailSubmit = async ({ email: inputEmail }: LoginParams) => {
        setEmail(inputEmail)
        const check = await api.auth.check(inputEmail)
        if (check.hasSAML) {
            setSsoInfo(check)
            setStep('email-options')
        } else {
            // No SAML, send magic link directly
            await api.auth.emailAuth(inputEmail, redirect)
            setMessage(t('login_email_confirmation'))
            setStep('email-sent')
        }
    }

    const handleSendMagicLink = async () => {
        await api.auth.emailAuth(email, redirect)
        setMessage(t('login_email_confirmation'))
        setStep('email-sent')
    }

    const handleSSOLogin = () => {
        handleRedirect('saml', email)
    }

    const handleBack = () => {
        setStep('methods')
        setEmail('')
        setSsoInfo(undefined)
        setMessage(undefined)
    }

    useEffect(() => {
        api.auth.methods().then((methods) => {
            setMethods(methods)
        }).catch(() => {})
    }, [])

    return (
        <div className="auth login">
            <div className="logo">
                <Logo />
            </div>

            {/* Step 1: Select auth method */}
            {step === 'methods' && (
                <div className="auth-step">
                    <h2>{t('welcome')}</h2>
                    {error && (
                        <Alert variant="error" title={t('error')}>{error}</Alert>
                    )}
                    <p>{t('login_select_method')}</p>
                    <div className="auth-methods">
                        {methods?.map((method) => (
                            <Button key={method.driver} onClick={() => handleMethod(method)}>{method.name}</Button>
                        ))}
                    </div>
                </div>
            )}

            {/* Basic auth: email + password */}
            {step === 'basic' && (
                <div className="auth-step">
                    <h2>{t('welcome')}</h2>
                    <p>{t('login_basic_instructions')}</p>
                    <FormWrapper<LoginParams>
                        onSubmit={handleBasicLogin}>
                        {form => <>
                            <TextInput.Field form={form} name="email" />
                            <TextInput.Field form={form} name="password" type="password" />
                        </>}
                    </FormWrapper>
                    <Button variant="plain" onClick={handleBack}>{t('back')}</Button>
                </div>
            )}

            {/* Email flow: enter email */}
            {step === 'email-input' && (
                <div className="auth-step">
                    <h2>{t('welcome')}</h2>
                    <p>{t('login_email_instructions')}</p>
                    <FormWrapper<LoginParams>
                        onSubmit={handleEmailSubmit}>
                        {form => <>
                            <TextInput.Field form={form} name="email" />
                        </>}
                    </FormWrapper>
                    <Button variant="plain" onClick={handleBack}>{t('back')}</Button>
                </div>
            )}

            {/* Email flow: SAML available, show options */}
            {step === 'email-options' && (
                <div className="auth-step">
                    <h2>{t('welcome')}</h2>
                    <p>{t('login_email_available_methods')}</p>
                    <div className="auth-methods">
                        <Button onClick={handleSSOLogin}>
                            {`Continue with ${ssoInfo?.samlName ?? 'SSO'}`}
                        </Button>
                        <Button variant="secondary" onClick={handleSendMagicLink}>
                            {t('send_magic_link')}
                        </Button>
                    </div>
                    <Button variant="plain" onClick={handleBack}>{t('back')}</Button>
                </div>
            )}

            {/* Email flow: magic link sent */}
            {step === 'email-sent' && (
                <div className="auth-step">
                    <h2>{t('welcome')}</h2>
                    <Alert variant="info" title={t('success')}>{message}</Alert>
                    <Button variant="plain" onClick={handleBack}>{t('back')}</Button>
                </div>
            )}
        </div>
    )
}
